import type { ReactNode } from "react";

import type { FormFieldLabelProps, FormFieldLabelVariantProps } from "@core/components/FormFieldLabel";
import { FormFieldLabel } from "@core/components/FormFieldLabel";
import type { SupportingTextVariant } from "@core/components/SupportingText";
import { SupportingText } from "@core/components/SupportingText";
import classnames from "classnames";

import styles from "./FormInputWrapper.module.scss";

export const FORM_INPUT_STATUSES = ["default", "error", "success"] as const;

export type FormInputStatus = (typeof FORM_INPUT_STATUSES)[number];

export type WithLabel = {
  labelFields: Pick<FormFieldLabelProps, "label" | "requiredText" | "optionalText" | "necessityIndicator">;
  "aria-label"?: string;
  "aria-labelledby"?: string;
};

export type WithAriaLabel = {
  labelFields?: never;
} & (
  | {
      "aria-label": string;
      "aria-labelledby"?: string;
    }
  | {
      "aria-label"?: string;
      "aria-labelledby": string;
    }
);

export type OmittedCommonFormFieldProps = "className" | "aria-label" | "aria-labelledby";

export type FormFieldPresentationProps = {
  value?: string | number;
  labelFields?: Pick<
    FormFieldLabelProps,
    "label" | "requiredText" | "optionalText" | "necessityIndicator" | "asterisk"
  >;
  labelId?: string;
  required?: boolean;
  disabled?: boolean;
  status?: FormInputStatus;
  helperText?: ReactNode;
  helperTextId?: string;
  inputId?: string;
  maxLength?: number;
  showCharacterCount?: boolean;
};

export type FormInputWrapperProps = FormFieldPresentationProps & {
  children: ReactNode;
};

const getSupportingTextVariant = (status: FormInputStatus): SupportingTextVariant => {
  if (status === "error") return "error";
  if (status === "success") return "success";
  return "info";
};

type LabelFields = NonNullable<FormFieldPresentationProps["labelFields"]>;

const hasNecessityIndicator = (
  labelFields: LabelFields
): labelFields is LabelFields & { necessityIndicator: true; asterisk?: never } =>
  labelFields.necessityIndicator === true;

const getLabelVariantProps = (labelFields: LabelFields): FormFieldLabelVariantProps => {
  if (hasNecessityIndicator(labelFields)) {
    return {
      necessityIndicator: true,
    };
  }

  return {
    asterisk: labelFields.asterisk ?? true,
  };
};

export const FormInputWrapper = ({
  labelFields,
  labelId,
  disabled = false,
  required,
  status = "default",
  helperText,
  helperTextId,
  maxLength,
  value,
  inputId,
  showCharacterCount = false,
  children,
}: FormInputWrapperProps) => {
  const characterCount = value?.toString().length ?? 0;
  const isFooterVisible = !!helperText || (showCharacterCount && maxLength !== undefined);

  const indicatorProps = labelFields && getLabelVariantProps(labelFields);

  const { label, optionalText, requiredText } = labelFields ?? {};

  return (
    <div
      className={classnames(styles.root, {
        [styles.disabled]: disabled,
      })}
    >
      {label && (
        <div className={styles.labelWrapper}>
          <FormFieldLabel
            {...indicatorProps}
            id={labelId}
            label={label}
            requiredText={requiredText}
            optionalText={optionalText}
            htmlFor={inputId}
            required={required}
          />
        </div>
      )}

      {children}

      {isFooterVisible && (
        <div className={styles.footer}>
          {helperText && (
            <SupportingText id={helperTextId} variant={getSupportingTextVariant(status)}>
              {helperText}
            </SupportingText>
          )}
          {showCharacterCount && maxLength !== undefined && maxLength > 0 && (
            <span className={styles.characterCount} dir="ltr" data-testid="form-input-count">
              {characterCount}/{maxLength}
            </span>
          )}
        </div>
      )}
    </div>
  );
};
